"use client";
import { useAtomValue, useAtom, atom } from "jotai";
import dynamic from "next/dynamic";
import {
  NodeMouseHandler,
  ReactFlow,
  SelectionMode,
  ConnectionMode,
} from "reactflow";
import { ReactNode, useCallback, useRef, useState } from "react";
import {
  cursorAtom,
  edgesAtom,
  isViewOnlyModeAtom,
  nodesAtom,
  selectedNodeTypeAtom,
  horizontalHelperLineAtom,
  verticalHelperLineAtom,
  isViewerAtom,
  isPublicAtom,
  togglesAtomResettable,
} from "@zapier/canvas-state";
import classNames from "classnames";

import isConnectingTwoZapSteps from "../../utils/isConnectingTwoZapSteps";
import { ThirdPartyIntegrations } from "../../components/ThirdPartyIntegrations";
import useRemoveTempNodesAndEdges from "../../hooks/useRemoveTempNodesAndEdges";
import { useCanvasEmbedEvents } from "../../hooks/useCanvasEmbedEvents";
import { areElementsSelectable } from "../../utils/elementsSelectable";

import { AssetDetailModal } from "../AssetDetailModal";

import { nodeTypes } from "./nodeTypes";
import { edgeTypes } from "./edgeTypes";
import { useReactFlowCallbacks } from "./useReactFlowCallbacks";
import { useDeleteCallbacks } from "./useDeleteCallbacks";
import { HelperLines } from "./HelperLines";
import { useOnNodeClick } from "./useOnNodeClick";
import { useOnSelection } from "./useOnSelection";
import { useOnPaneClick } from "./useOnPaneClick";
import ErrorBoundary from "./ErrorBoundary";
import { getCursorType } from "./utils";
import { useCopyPaste } from "./useCopyPaste";

const Sidebar = dynamic(() => import("./Sidebar").then((mod) => mod.Sidebar), {
  ssr: false,
});

type Props = {
  children: ReactNode;
};

const selectedAssetIdAtom = atom<string | null>(null);
const assetTypeAtom = atom<"zap" | "table" | "interface" | null>(null);
const pageSlugAtom = atom<string | null>(null);

export function Canvas({ children }: Props) {
  const cursorMode = useAtomValue(cursorAtom);
  const nodes = useAtomValue(nodesAtom);
  const edges = useAtomValue(edgesAtom);
  const [nodeId, setNodeId] = useState<string | null>(null);
  const { canvasAssetModal } = useAtomValue(togglesAtomResettable);

  const ref = useRef<HTMLDivElement | null>(null);
  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);
  const isViewer = useAtomValue(isViewerAtom);
  const isPublic = useAtomValue(isPublicAtom);
  const selectedNodeType = useAtomValue(selectedNodeTypeAtom);
  const hasSelectedNodeType = Boolean(selectedNodeType);

  useCopyPaste();

  const helperLineHorizontal = useAtomValue(horizontalHelperLineAtom);
  const helperLineVertical = useAtomValue(verticalHelperLineAtom);
  const [selectedAssetId, setSelectedAssetId] = useAtom(selectedAssetIdAtom);
  const [assetType, setAssetType] = useAtom(assetTypeAtom);
  const [pageId, setPageId] = useAtom(pageSlugAtom);
  const {
    onNodesChange,
    onEdgesChange,
    onNodeContextMenu,
    onPaneMouseMove,
    onInitSetViewport,
    onConnect,
    onNodeDragStart,
    onNodeDrag,
    onNodeDragStop,
    onConnectStart,
    onConnectEnd,
    onReconnectStart,
    onReconnectEnd,
    onReconnect,
  } = useReactFlowCallbacks(ref);
  const { onNodeClick } = useOnNodeClick(ref);
  const { onSelectionStart, onSelectionEnd } = useOnSelection();
  const { onPaneClick } = useOnPaneClick(ref);

  useRemoveTempNodesAndEdges();

  const { onNodeDeleteFromKeyboard, onEdgesDeleteFromKeyboard } =
    useDeleteCallbacks();

  useCanvasEmbedEvents();

  const onNodeDoubleClick: NodeMouseHandler = useCallback(
    (event, node) => {
      setNodeId(node.id);
      // Don't open the modal in production just yet.
      // I just want to see how this behaves when deployed behind zapier-staging.com
      if (!canvasAssetModal) {
        return;
      }
      if (node.data.zapId) {
        setSelectedAssetId(node.data.zapId);
        setAssetType("zap");
      }
      if (node.data.tableId) {
        setSelectedAssetId(node.data.tableId);
        setAssetType("table");
      }
      if (node.data.projectId && node.data.pageId) {
        setSelectedAssetId(node.data.projectId);
        setAssetType("interface");
        setPageId(node.data.pageId);
      }
    },
    [setSelectedAssetId, setAssetType, canvasAssetModal, setPageId],
  );

  return (
    <ErrorBoundary>
      {selectedAssetId && nodeId && (
        <AssetDetailModal
          selectedAssetId={selectedAssetId}
          setSelectedAssetId={setSelectedAssetId}
          assetType={assetType}
          setAssetType={setAssetType}
          pageId={pageId}
          nodeId={nodeId}
        />
      )}
      <ThirdPartyIntegrations />
      <ReactFlow
        ref={ref}
        connectionMode={ConnectionMode.Loose}
        nodes={nodes}
        edges={edges}
        elementsSelectable={areElementsSelectable({
          isViewOnlyMode,
          isViewer,
          isPublic,
        })}
        proOptions={{ hideAttribution: true }}
        selectionOnDrag={true}
        onNodesDelete={onNodeDeleteFromKeyboard}
        onEdgesDelete={onEdgesDeleteFromKeyboard}
        minZoom={0.25}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        panOnScroll={cursorMode !== "pan"}
        onNodeContextMenu={onNodeContextMenu}
        onPaneClick={onPaneClick}
        onPaneMouseMove={onPaneMouseMove}
        onMoveStart={() => {
          /* To prevent this bug in floating toolbar where popover menu is disconnected from the input
          when you pan: https://cdn.zappy.app/f78f666d913ec41747b8c4867e3ec1d9.gif */
          window.dispatchEvent(new Event("scroll", { bubbles: true }));
        }}
        onSelectionStart={onSelectionStart}
        onSelectionEnd={onSelectionEnd}
        onInit={onInitSetViewport}
        onNodeClick={onNodeClick}
        className={classNames(
          { "view-only-mode": isViewOnlyMode },
          {
            [`${getCursorType(cursorMode, hasSelectedNodeType)}-cursor`]:
              getCursorType(cursorMode, hasSelectedNodeType),
          },
        )}
        panOnDrag={
          cursorMode === "pan" ||
          (selectedNodeType !== null && selectedNodeType !== "group")
        }
        selectionMode={SelectionMode.Partial}
        onNodesChange={onNodesChange}
        onNodeDoubleClick={onNodeDoubleClick}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onConnectStart={onConnectStart}
        onConnectEnd={onConnectEnd}
        onNodeDragStart={onNodeDragStart}
        onNodeDrag={onNodeDrag}
        onNodeDragStop={onNodeDragStop}
        onReconnectStart={onReconnectStart}
        onReconnectEnd={onReconnectEnd}
        nodeDragThreshold={10}
        connectionRadius={100}
        isValidConnection={(connection) => !isConnectingTwoZapSteps(connection)}
        deleteKeyCode={["Backspace", "Delete"]}
        snapToGrid={true}
        snapGrid={[10, 10]}
        nodesDraggable={!isViewOnlyMode}
        edgesUpdatable={!isViewOnlyMode}
        onReconnect={onReconnect}
      >
        {children}
        <HelperLines
          horizontal={helperLineHorizontal}
          vertical={helperLineVertical}
        />
      </ReactFlow>
      {/* This for the React portal to attach to.
      This is so events are no longer trapped by ReactFlow */}
      <div id="toolbar-content" />
      <Sidebar />
    </ErrorBoundary>
  );
}
